import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LocalAgentService } from '../../core/services/local-agent.service';
import { UiHelperService } from '../../services/ui-helper.service';

export type TokenFisicoPinDialogMode = 'consultar' | 'restore' | 'restoreAfterOtp';

export interface TokenFisicoPinDialogData {
  mode: TokenFisicoPinDialogMode;
  tokenLabel: string;
  slotId: number;
  /** En modo restore: si se proporciona, se salta el paso de PIN actual (ya validado al listar certificados del token). */
  initialPin?: string;
}

export interface TokenFisicoPinDialogResult {
  pin?: string;
  newPin?: string;
}

/** Para restore: paso 1 = validar PIN actual, paso 2 = nuevo PIN + confirmar. restoreAfterOtp solo muestra paso 2. */
type RestoreStep = 1 | 2;

@Component({
  selector: 'app-token-fisico-pin-dialog',
  templateUrl: './token-fisico-pin-dialog.component.html',
  styleUrls: ['./token-fisico-pin-dialog.component.css'],
  standalone: false
})
export class TokenFisicoPinDialogComponent implements OnInit, OnChanges {
  pin = '';
  newPin = '';
  confirmPin = '';
  pinVisible = false;
  newPinVisible = false;
  confirmPinVisible = false;
  validationMessage = '';

  /** Solo en modo restore: 1 = PIN actual, 2 = nuevo PIN + confirmar */
  restoreStep: RestoreStep = 1;
  validatingPin = false;

  readonly minLen = 4;
  readonly maxLen = 16;

  /** Cuando true, el contenido se muestra en el espacio de trabajo (sin overlay de diálogo). */
  @Input() embedded = false;
  @Input() embeddedData: TokenFisicoPinDialogData | null = null;

  @Output() embeddedCancel = new EventEmitter<void>();
  @Output() embeddedResult = new EventEmitter<TokenFisicoPinDialogResult>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<TokenFisicoPinDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public injectedData: TokenFisicoPinDialogData | null,
    private localAgentService: LocalAgentService,
    private uiHelper: UiHelperService
  ) {}

  get pinData(): TokenFisicoPinDialogData {
    const d = this.embeddedData ?? this.injectedData;
    if (!d) {
      throw new Error('TokenFisicoPinDialog: faltan datos (embeddedData o MAT_DIALOG_DATA).');
    }
    return d;
  }

  ngOnInit(): void {
    this.applyInitialRestoreStep();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embeddedData'] && this.embeddedData) {
      this.applyInitialRestoreStep();
    }
  }

  private applyInitialRestoreStep(): void {
    const d = this.embeddedData ?? this.injectedData;
    if (!d) return;
    if (d.mode === 'restore' && d.initialPin != null &&
        d.initialPin.length >= this.minLen && d.initialPin.length <= this.maxLen) {
      this.restoreStep = 2;
    }
  }

  get isConsultar(): boolean {
    return this.pinData.mode === 'consultar';
  }

  get isRestore(): boolean {
    return this.pinData.mode === 'restore';
  }

  /** Tras validar OTP: solo nuevo PIN + confirmar, sin pedir PIN actual. */
  get isRestoreAfterOtp(): boolean {
    return this.pinData.mode === 'restoreAfterOtp';
  }

  get isRestoreStep1(): boolean {
    return this.isRestore && this.restoreStep === 1;
  }

  get isRestoreStep2(): boolean {
    return (this.isRestore && this.restoreStep === 2) || this.isRestoreAfterOtp;
  }

  get confirmDisabled(): boolean {
    if (this.isConsultar) {
      return this.pin.length < this.minLen;
    }
    if (this.isRestoreStep1) {
      return this.pin.length < this.minLen || this.pin.length > this.maxLen || this.validatingPin;
    }
    if (this.isRestoreStep2) {
      if (this.newPin.length < this.minLen || this.confirmPin.length < this.minLen) return true;
      if (this.newPin.length > this.maxLen || this.confirmPin.length > this.maxLen) return true;
      return this.newPin !== this.confirmPin;
    }
    return true;
  }

  get confirmLabel(): string {
    if (this.isConsultar) return 'Consultar';
    if (this.isRestoreStep1) return 'Continuar';
    return 'Restablecer PIN';
  }

  get title(): string {
    if (this.isConsultar) return 'Consultar contenido';
    if (this.isRestoreStep1) return 'Restablecer contraseña (PIN)';
    if (this.isRestoreAfterOtp) return 'Establecer nuevo PIN';
    return 'Nuevo PIN';
  }

  private finishWithResult(r: TokenFisicoPinDialogResult): void {
    if (this.dialogRef) {
      this.dialogRef.close(r);
    } else {
      this.embeddedResult.emit(r);
    }
  }

  confirm(): void {
    this.validationMessage = '';
    if (this.isConsultar) {
      if (this.pin.length < this.minLen) {
        this.validationMessage = 'El PIN debe tener al menos 4 caracteres.';
        return;
      }
      if (this.pin.length > this.maxLen) {
        this.validationMessage = 'El PIN debe tener entre 4 y 16 caracteres.';
        return;
      }
      this.finishWithResult({ pin: this.pin });
      return;
    }
    if (this.isRestoreStep1) {
      this.validateCurrentPinAndGoToStep2();
      return;
    }
    if (this.isRestoreStep2 || this.isRestoreAfterOtp) {
      if (this.newPin.length < this.minLen || this.newPin.length > this.maxLen) {
        this.validationMessage = 'El PIN debe tener entre 4 y 16 caracteres.';
        return;
      }
      if (this.newPin !== this.confirmPin) {
        this.validationMessage = 'El nuevo PIN y la confirmación no coinciden.';
        return;
      }
      this.finishWithResult({ newPin: this.newPin });
    }
  }

  /** Paso 1 restore: valida PIN actual con getTokenContent y pasa al paso 2. */
  private validateCurrentPinAndGoToStep2(): void {
    if (this.pin.length < this.minLen || this.pin.length > this.maxLen) {
      this.validationMessage = 'El PIN debe tener entre 4 y 16 caracteres.';
      return;
    }
    this.validatingPin = true;
    this.validationMessage = '';
    this.uiHelper.showSpinner('Validando PIN del token…', 'admin');
    this.localAgentService.getTokenContent(this.pin, this.pinData.slotId).subscribe({
      next: () => {
        this.validatingPin = false;
        this.uiHelper.hideSpinner();
        this.restoreStep = 2;
        this.validationMessage = '';
      },
      error: (err) => {
        this.validatingPin = false;
        this.uiHelper.hideSpinner();
        this.validationMessage = err?.error?.message || err?.message || 'El PIN actual no es correcto.';
      }
    });
  }

  /** En paso 2, volver al paso 1 (cambiar PIN actual). */
  backToStep1(): void {
    this.restoreStep = 1;
    this.validationMessage = '';
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.embeddedCancel.emit();
    }
  }

  // ——— Robustez del nuevo PIN (informativo; el token sigue aceptando 4–16 caracteres) ———

  get newPinHasMinLength(): boolean {
    return this.newPin.length >= this.minLen;
  }

  get newPinHasLength8(): boolean {
    return this.newPin.length >= 8;
  }

  get newPinHasDigit(): boolean {
    return /\d/.test(this.newPin);
  }

  get newPinHasLetter(): boolean {
    return /[a-zA-Z]/.test(this.newPin);
  }

  get newPinHasMixedCase(): boolean {
    return /[a-z]/.test(this.newPin) && /[A-Z]/.test(this.newPin);
  }

  get newPinHasSpecial(): boolean {
    return /[^a-zA-Z0-9]/.test(this.newPin);
  }

  /** 0–100 según criterios opcionales de fortaleza */
  get newPinStrengthPercent(): number {
    if (!this.newPin.length) return 0;
    let n = 0;
    if (this.newPinHasMinLength) n++;
    if (this.newPinHasLength8) n++;
    if (this.newPinHasDigit) n++;
    if (this.newPinHasLetter) n++;
    if (this.newPinHasMixedCase) n++;
    if (this.newPinHasSpecial) n++;
    return Math.round((n / 6) * 100);
  }

  get newPinStrengthClass(): 'none' | 'weak' | 'fair' | 'good' | 'strong' {
    const p = this.newPinStrengthPercent;
    if (!this.newPin.length) return 'none';
    if (p <= 33) return 'weak';
    if (p <= 50) return 'fair';
    if (p <= 75) return 'good';
    return 'strong';
  }

  get newPinStrengthLabel(): string {
    if (!this.newPin.length) return '';
    switch (this.newPinStrengthClass) {
      case 'weak':
        return 'Seguridad: baja';
      case 'fair':
        return 'Seguridad: aceptable';
      case 'good':
        return 'Seguridad: buena';
      case 'strong':
        return 'Seguridad: alta';
      default:
        return '';
    }
  }

  get pinsMatchHint(): 'match' | 'mismatch' | 'empty' {
    if (!this.confirmPin.length) return 'empty';
    return this.newPin === this.confirmPin ? 'match' : 'mismatch';
  }
}
